import { applyPreset, moveColumnTo, placeAsNewColumn, placeInColumn, removeField } from '../views/columnOps';
import type { FieldKey, ViewColumn } from '../views/types';
import { IsAllowed, removeVisibleColumn, toFullGapIndex, toFullMemberIndex, visibleColumns, visiblePreset } from './fieldAccess';
import type { MemberTarget } from './useColumnDnd';

/**
 * What the "Obsah tabulky" sidebar does to a tab's columns.
 *
 * The user sees only the fields they may see, at the positions they see them; the stored columns may
 * hold more. Each action is translated onto the stored columns here, so a field the user can't see
 * keeps its place whatever they do around it.
 */

const sameStack = (a: FieldKey[], b: FieldKey[]) => a.length === b.length && a.every((key, i) => key === b[i]);

/** Whether a field is in the table at all — the switch next to it in "Dostupné sloupce". */
export const isFieldShown = (columns: ViewColumn[], key: FieldKey): boolean => columns.some(column => column.members.includes(key));

/** The column that is this preset, as the user sees it (forbidden members aside), if there is one. */
export const presetColumnId = (columns: ViewColumn[], preset: FieldKey[], isAllowed: IsAllowed): string | undefined => {
  const visible = visiblePreset(preset, isAllowed);
  if (visible.length === 0) return undefined;
  return visibleColumns(columns, isAllowed).find(column => sameStack(column.members, visible))?.id;
};

/** Switch on: the field becomes the last column. Switch off: it leaves the table. */
export const toggleField = (columns: ViewColumn[], key: FieldKey, on: boolean): ViewColumn[] =>
  on ? (isFieldShown(columns, key) ? columns : placeAsNewColumn(columns, [key])) : removeField(columns, key);

/**
 * Switch on: the preset's visible fields become one new last column, taken from wherever they were.
 * Switch off: the column that is the preset goes, keeping any member the user can't see.
 */
export const togglePreset = (columns: ViewColumn[], preset: FieldKey[], on: boolean, isAllowed: IsAllowed): ViewColumn[] => {
  const existing = presetColumnId(columns, preset, isAllowed);
  if (on) return existing ? columns : applyPreset(columns, visiblePreset(preset, isAllowed));
  return existing ? removeVisibleColumn(columns, existing, isAllowed) : columns;
};

/**
 * What switching a preset on would take from other columns: its visible fields that already sit in
 * the table, grouped by the column they'd leave. A preset brings Ringil's whole column, so the user is
 * told beforehand which of their columns it breaks up. Nothing while the preset is already on.
 */
export const presetTakesFrom = (columns: ViewColumn[], preset: FieldKey[], isAllowed: IsAllowed): { columnId: string; fields: FieldKey[] }[] => {
  if (presetColumnId(columns, preset, isAllowed)) return [];
  const wanted = new Set(visiblePreset(preset, isAllowed));
  return visibleColumns(columns, isAllowed)
    .map(column => ({ columnId: column.id, fields: column.members.filter(key => wanted.has(key)) }))
    .filter(taken => taken.fields.length > 0);
};

/** A field dropped onto a column: it stacks there, at the position the user dropped it. */
export const dropFieldOnColumn = (columns: ViewColumn[], key: FieldKey, target: MemberTarget, isAllowed: IsAllowed): ViewColumn[] => {
  const column = columns.find(c => c.id === target.columnId);
  if (!column) return columns;
  // Measured without the dragged field when it already sits in this column, as placeInColumn expects.
  return placeInColumn(columns, key, column.id, toFullMemberIndex(column, target.index, isAllowed));
};

/** A field dropped between columns: a column of its own there. */
export const dropFieldOnGap = (columns: ViewColumn[], key: FieldKey, gap: number, isAllowed: IsAllowed): ViewColumn[] =>
  placeAsNewColumn(columns, [key], toFullGapIndex(columns, gap, isAllowed));

/** A column dropped between columns: it moves there. */
export const dropColumnOnGap = (columns: ViewColumn[], columnId: string, gap: number, isAllowed: IsAllowed): ViewColumn[] =>
  moveColumnTo(columns, columnId, toFullGapIndex(columns, gap, isAllowed));

/** "×" on a column card: what the user sees of it goes; fields they can't see stay. */
export const removeColumnFromSidebar = (columns: ViewColumn[], columnId: string, isAllowed: IsAllowed): ViewColumn[] => removeVisibleColumn(columns, columnId, isAllowed);

/** What the user drags in the order list: a field, a column of several fields, or a column of one field. */
export type DragKind = 'field' | 'column' | 'singleFieldColumn';

export type CardDropTarget = { kind: 'gap'; index: number } | { kind: 'member'; target: MemberTarget };

/**
 * Where a drag over a column card (outside its field rows) would drop. A field joins the column. A
 * column goes before or after the card, by which half is hovered — except a column of one field: that
 * is a field the user pulled out of a stack, and has to be able to go back in. Its middle stacks it
 * into the card, only the outer quarters reorder.
 */
export const cardDropTarget = (args: {
  drag: DragKind;
  /** The dragged column is this card. */
  self: boolean;
  /** Pointer position within the card, 0 (top) to 1 (bottom). */
  fraction: number;
  columnId: string;
  columnIndex: number;
  memberCount: number;
}): CardDropTarget => {
  const { drag, self, fraction, columnId, columnIndex, memberCount } = args;
  const append: CardDropTarget = { kind: 'member', target: { columnId, index: memberCount } };
  if (drag === 'field') return append;
  if (drag === 'singleFieldColumn' && !self) {
    if (fraction < 0.25) return { kind: 'gap', index: columnIndex };
    if (fraction > 0.75) return { kind: 'gap', index: columnIndex + 1 };
    return append;
  }
  return { kind: 'gap', index: fraction > 0.5 ? columnIndex + 1 : columnIndex };
};
